import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Building2, CircleUser } from 'lucide-react';

import {
  getCompanyQueryKey,
  loadQuery as queryCompanies,
} from 'common/actions/thirdPartyCompanyQueries';
import { getUserQueryKey, loadQuery as queryUsers } from 'common/actions/userQueries';
import {
  getUsersForCompanyQueryKey,
  loadUsersForCompanyQuery as queryUsersForCompany,
} from 'common/actions/usersForCompanyQueries';
import UserAvatarV2 from 'common/common/UserAvatarV2';
import connect from 'common/core/connect';
import HStack from 'common/ui/HStack';
import { Select, type SelectBaseOption } from 'common/uiV3/Select';
import { isNotNil } from 'common/util/isNil';
import useDelayer from 'common/util/useDelayer';

import type { User } from 'common/api/endpoints/users';
import type { Option } from 'common/ui/common/select/SelectCommon';
import type { State } from 'redux-connect';

type UserSelectProps = {
  isLoading: boolean;
  onChange: (value: string | undefined) => void;
  onSearchChange: (value: string | undefined) => Promise<SelectBaseOption[]>;
  options: SelectBaseOption[];
  selectedUserID: string | undefined;
};

const UserSelect = ({
  options,
  onChange,
  onSearchChange,
  selectedUserID,
  isLoading,
}: UserSelectProps) => (
  <Select
    options={options}
    placeholder={
      <>
        <CircleUser /> User
      </>
    }
    value={selectedUserID}
    onValueChange={onChange}
    onSearchChange={onSearchChange}
    isSearchable
    fullWidth
    isLoading={isLoading}
    allowClear
  />
);

type CompanySelectProps = {
  isLoading: boolean;
  onChange: (value: string | undefined) => void;
  onSearchChange: (value: string | undefined) => Promise<SelectBaseOption[]>;
  options: SelectBaseOption[];
  selectedCompanyID: string | undefined;
};

const CompanySelect = ({
  isLoading,
  options,
  onChange,
  onSearchChange,
  selectedCompanyID,
}: CompanySelectProps) => (
  <Select
    options={options}
    placeholder={
      <>
        <Building2 /> Company
      </>
    }
    isLoading={isLoading}
    value={selectedCompanyID}
    onValueChange={onChange}
    onSearchChange={onSearchChange}
    isSearchable
    fullWidth
    allowClear
  />
);

export type OnUserCompanyChange = ({
  thirdPartyCompanyID,
  userID,
}: {
  thirdPartyCompanyID: string | undefined;
  userID: string | undefined;
}) => void;

type OwnProps = {
  defaultUser?: Option;
  defaultCompany?: Option;
  onChange: OnUserCompanyChange;
};

type ConnectStateProps = {
  thirdPartyCompanies: any;
  thirdPartyCompanyQueries: any;
  users: any;
  usersForCompanyQueries: any;
  userQueries: any;
};

type ConnectDispatchProps = {
  searchCompanies: (queryParams: { search: string; userID?: string }) => void;
  searchUsers: (queryParams: { search: string }) => void;
  searchUsersForCompany: (queryParams: { search: string; thirdPartyCompanyID: string }) => void;
};

type UserCompanySelectProps = OwnProps & ConnectStateProps & ConnectDispatchProps;

const UserCompanySelect = (props: UserCompanySelectProps & OwnProps) => {
  const {
    defaultUser,
    defaultCompany,
    onChange,
    searchCompanies,
    searchUsers,
    searchUsersForCompany,
    thirdPartyCompanies,
    thirdPartyCompanyQueries,
    users,
    usersForCompanyQueries,
    userQueries,
  } = props;

  const [userSearchValue, setUserSearchValue] = useState('');
  const [companySearchValue, setCompanySearchValue] = useState('');
  const [selectedUserID, setSelectedUserID] = useState<string | undefined>(defaultUser?.value);

  const [userLoading, setUserLoading] = useState(false);
  const [companyLoading, setCompanyLoading] = useState(false);
  const [selectedCompanyID, setSelectedCompanyID] = useState<string | undefined>(
    defaultCompany?.value,
  );

  const [autoAssignCompanyFlag, setAutoAssignCompanyFlag] = useState(false);

  const performUserSearch = useCallback(
    async (value: string, thirdPartyCompanyID?: string) => {
      setUserLoading(true);
      if (thirdPartyCompanyID) {
        await searchUsersForCompany({ search: value, thirdPartyCompanyID });
      } else {
        await searchUsers({ search: value });
      }
      setUserLoading(false);
    },
    [searchUsers, searchUsersForCompany, setUserLoading],
  );

  const performCompanySearch = useCallback(
    async (value: string, userID?: string) => {
      setCompanyLoading(true);
      await searchCompanies({ search: value, userID });
      setCompanyLoading(false);
    },
    [searchCompanies, setCompanyLoading],
  );

  const onUserSearchChange = useCallback(
    async (value) => {
      setUserSearchValue(value);
      performUserSearch(value, selectedCompanyID);
      return [];
    },
    [performUserSearch, selectedCompanyID],
  );

  const onCompanySearchChange = useCallback(
    async (value) => {
      if (selectedCompanyID) {
        return;
      }

      setCompanySearchValue(value);
      performCompanySearch(value, selectedUserID);
    },
    [performCompanySearch, selectedCompanyID, selectedUserID],
  );

  const onUserChange = useCallback(
    async (newUserID: string | undefined) => {
      setSelectedUserID(newUserID);
      setUserSearchValue('');
      onChange({ thirdPartyCompanyID: selectedCompanyID, userID: newUserID });

      if (newUserID && !selectedCompanyID) {
        performCompanySearch('', newUserID);
      }
      setAutoAssignCompanyFlag(true);
    },
    [onChange, performCompanySearch, selectedCompanyID],
  );

  const onCompanyChange = useCallback(
    async (newCompanyID: string | undefined) => {
      setSelectedCompanyID(newCompanyID);
      setCompanySearchValue('');
      onChange({ thirdPartyCompanyID: newCompanyID, userID: selectedUserID });

      if (newCompanyID && !selectedUserID) {
        performUserSearch('', newCompanyID);
      }
    },
    [onChange, performUserSearch, selectedUserID],
  );

  useEffect(() => {
    // Load initial users and companies on mount
    performUserSearch('', selectedCompanyID);
    performCompanySearch('', selectedUserID);
  }, [performCompanySearch, performUserSearch, selectedCompanyID, selectedUserID]);

  const userQueryKey = selectedCompanyID
    ? getUsersForCompanyQueryKey({
        search: userSearchValue,
        thirdPartyCompanyID: selectedCompanyID,
      })
    : getUserQueryKey({
        search: userSearchValue,
      });
  const userQueryResult = selectedCompanyID
    ? usersForCompanyQueries[userQueryKey]
    : userQueries[userQueryKey];
  const userIDs = useMemo(() => {
    if (selectedCompanyID) {
      return userQueryResult?.items?.map((item: { _id: string }) => item._id) || [];
    }

    return userQueryResult?.userIDs || [];
  }, [selectedCompanyID, userQueryResult]);
  const userOptions = useMemo<SelectBaseOption[]>(() => {
    return userIDs
      .map((userID: string) => {
        const user = users[userID];
        if (!user) {
          return null;
        }

        return {
          label: (
            <HStack className="items-center gap-2">
              <UserAvatarV2 className="shrink-0" user={user} size={16} />
              <div className="truncate">{user.name}</div>
            </HStack>
          ),
          value: user._id,
        };
      })
      .filter(isNotNil);
  }, [userIDs, users]);

  const companyQueryKey = getCompanyQueryKey({
    search: companySearchValue,
    userID: selectedUserID,
  });
  const companyQueryResult = thirdPartyCompanyQueries[companyQueryKey];
  const companyOptions = useMemo<SelectBaseOption[]>(
    () =>
      companyQueryResult?.companyIDs?.map((companyID: string) => {
        const tpc = thirdPartyCompanies[companyID];
        return {
          label: tpc.name,
          value: tpc.thirdPartyCompanyID,
        };
      }) || [],
    [companyQueryResult, thirdPartyCompanies],
  );

  useEffect(() => {
    if (!autoAssignCompanyFlag) {
      return;
    }

    if (companyOptions.length === 1) {
      onCompanyChange(companyOptions[0].value);
    }

    if (companyOptions.length >= 1) {
      setAutoAssignCompanyFlag(false);
    }
  }, [autoAssignCompanyFlag, companyOptions, onCompanyChange]);

  return (
    <>
      <UserSelect
        isLoading={userLoading}
        onChange={onUserChange}
        onSearchChange={useDelayer(onUserSearchChange, 500)}
        options={userOptions}
        selectedUserID={selectedUserID}
      />
      <CompanySelect
        isLoading={companyLoading}
        onChange={onCompanyChange}
        onSearchChange={useDelayer(onCompanySearchChange, 500)}
        options={companyOptions}
        selectedCompanyID={selectedCompanyID}
      />
    </>
  );
};

const connectDispatchProps: ConnectDispatchProps = {
  searchUsers: queryUsers,
  searchUsersForCompany: queryUsersForCompany,
  searchCompanies: queryCompanies,
};

export default connect(
  (state: State): ConnectStateProps => ({
    thirdPartyCompanies: state.thirdPartyCompanies,
    thirdPartyCompanyQueries: state.thirdPartyCompanyQueries,
    users: Object.values(state.users).reduce(
      (acc: Record<string, User>, user) => {
        const typedUser = user as User;
        acc[typedUser._id] = typedUser;
        return acc;
      },
      {} as Record<string, User>,
    ),
    usersForCompanyQueries: state.usersForCompanyQueries,
    userQueries: state.userQueries,
  }),
  connectDispatchProps,
)(UserCompanySelect) as unknown as React.FC<OwnProps>;
